import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';

export interface BirthdayParts {
  day: number | null;
  month: number | null;
  year: number | null;
}

/**
 * The three-part day / month / year input the public registration form uses for a
 * date of birth, and the month and year lists it offers.
 *
 * `date` is null until all three parts are filled in, so a half-entered birthday is
 * never mistaken for a real one.
 */
export function useBirthdayInput(locale: MaybeRefOrGetter<string>) {
  const birthday = ref<BirthdayParts>({ day: null, month: null, year: null });

  const date = computed<Date | null>(() => {
    const { day, month, year } = birthday.value;

    // month is a zero-based index, so 0 is January and has to pass this check
    if (!year || !day || !Number.isInteger(month)) {
      return null;
    }

    return new Date(Date.UTC(year, month as number, day));
  });

  const months = computed<string[]>(() => {
    const formatter = new Date();
    formatter.setDate(1);

    return Array.from({ length: 12 }, (_, month) => {
      formatter.setMonth(month);

      return formatter.toLocaleString(toValue(locale), { month: 'long' });
    });
  });

  const years = computed<number[]>(() => {
    const current = new Date().getFullYear();

    return Array.from({ length: 101 }, (_, offset) => current - offset);
  });

  return { birthday, date, months, years };
}
